import { Component } from '@theme/component';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * @typedef {Object} ShowMoreRefs
 * @property {HTMLElement} showMoreButton - The button to toggle visibility of the items
 * @property {HTMLElement[]} showMoreItems - The hidden items to show and hide
 * @property {HTMLElement} showMoreContent - The content container to measure and animate
 * @property {HTMLScriptElement | undefined} showMoreData - Optional JSON payload for lazily rendered items
 */

/**
 * A custom element that manages the showing and hiding excess content items
 *
 * @extends {Component<ShowMoreRefs>}
 */

class ShowMoreComponent extends Component {
  requiredRefs = ['showMoreButton', 'showMoreContent'];

  /**
   * @type {boolean}
   */
  #expanded = false;

  /**
   * @type {boolean}
   */
  #disableOnDesktop = false;

  /**
   * @type {number}
   */
  #collapsedHeight = 0;

  /**
   * @type {'mobile:hidden' | 'hidden'}
   */
  #disabledClass = 'hidden';

  /**
   * @type {'MOBILE' | 'DESKTOP'}
   */
  get #currentBreakpoint() {
    return isMobileBreakpoint() ? 'MOBILE' : 'DESKTOP';
  }

  /**
   * @type {Animation | undefined}
   */
  #animation;

  /**
   * @constant {number}
   */
  #animationSpeed = 300;

  connectedCallback() {
    super.connectedCallback();
    this.#updateBreakpointState();
  }

  /**
   * Updates the current breakpoint and apprpropriate disabled class
   */
  #updateBreakpointState = () => {
    this.#disableOnDesktop = this.dataset.disableOnDesktop === 'true';
    this.#disabledClass = this.#disableOnDesktop ? 'mobile:hidden' : 'hidden';
  };

  /**
   * Handles expanding the content
   * @returns {{startHeight: number, endHeight: number}}
   */
  #expand = () => {
    this.#hydrateLazyItems();
    const { showMoreItems = [], showMoreContent } = this.refs;

    this.#collapsedHeight = showMoreContent.offsetHeight;
    const startHeight = this.#collapsedHeight;

    showMoreItems?.forEach((item) => item.classList.remove(this.#disabledClass));

    return {
      startHeight,
      endHeight: showMoreContent.scrollHeight,
    };
  };

  /**
   * Handles collapsing the content
   * @returns {{startHeight: number, endHeight: number}}
   */
  #collapse = () => {
    const { showMoreContent } = this.refs;
    const startHeight = showMoreContent.offsetHeight;
    const endHeight = this.#collapsedHeight;

    return { startHeight, endHeight };
  };

  /**
   * Initializes a height transition
   * @param {number} startHeight
   * @param {number} endHeight
   */
  #animateHeight = (startHeight, endHeight) => {
    const { showMoreContent } = this.refs;

    showMoreContent.style.overflow = 'hidden';
    this.#animation?.cancel();

    this.#animation = showMoreContent.animate(
      {
        height: [`${startHeight}px`, `${endHeight}px`],
      },
      {
        duration: this.#animationSpeed,
        easing: 'ease-in-out',
      }
    );

    this.#animation.onfinish = () => this.#onAnimationFinish();
  };

  /**
   * Handles the animation finish event.
   */
  #onAnimationFinish() {
    const { showMoreContent, showMoreItems = [] } = this.refs;

    if (this.#expanded) {
      showMoreItems.forEach((item) => item.classList.add(this.#disabledClass));
    }

    showMoreContent.style.removeProperty('height');
    showMoreContent.style.overflow = '';
    this.#expanded = !this.#expanded;
  }

  /**
   * Builds deferred standard checkbox rows on first expansion. Liquid keeps
   * the payload compact while the native facet component continues handling
   * change, keyboard and prefetch events for the inserted inputs.
   */
  #hydrateLazyItems() {
    const dataElement = this.querySelector('script[ref="showMoreData"]');
    if (!(dataElement instanceof HTMLScriptElement) || dataElement.dataset.hydrated === 'true') return;

    const list = this.refs.showMoreContent.querySelector('.facets__inputs-list');
    const prototype = list?.querySelector('.facets__inputs-list-item');
    if (!(list instanceof HTMLUListElement) || !(prototype instanceof HTMLLIElement)) return;

    let items;
    try {
      items = JSON.parse(dataElement.textContent || '[]');
    } catch (error) {
      console.error('Unable to hydrate deferred filter values', error);
      return;
    }

    if (!Array.isArray(items)) return;

    const hydratedItems = [];
    for (const item of items) {
      const row = /** @type {HTMLLIElement} */ (prototype.cloneNode(true));
      const input = row.querySelector('input[type="checkbox"]');
      const label = row.querySelector('label[for]');
      const labelText = row.querySelector('.checkbox__label-text');
      if (!(input instanceof HTMLInputElement) || !(label instanceof HTMLLabelElement)) continue;

      row.classList.remove('hidden', 'mobile:hidden');
      row.classList.add(this.#disabledClass);
      row.classList.toggle('facets__inputs-list-item--disabled', Boolean(item.disabled));
      row.setAttribute('ref', 'showMoreItems[]');

      input.name = String(item.name || '');
      input.value = String(item.value || '');
      input.id = String(item.id || '');
      input.checked = false;
      input.disabled = Boolean(item.disabled);
      input.dataset.label = String(item.label || '');
      input.setAttribute('ref', 'facetInputs[]');

      label.htmlFor = input.id;
      if (labelText) labelText.textContent = String(item.label || '');

      list.append(row);
      hydratedItems.push(row);
    }

    dataElement.dataset.hydrated = 'true';
    this.refs.showMoreItems = [...(this.refs.showMoreItems || []), ...hydratedItems];
  }

  /**
   * Toggles the expansion state of the content.
   *
   * @param {Event} event - The click event
   */
  toggle = (event) => {
    event.preventDefault();

    this.#updateBreakpointState();

    if (this.#currentBreakpoint === 'DESKTOP' && this.#disableOnDesktop) return;

    const { startHeight, endHeight } = !this.#expanded ? this.#expand() : this.#collapse();

    this.dataset.expanded = this.#expanded ? 'false' : 'true';
    this.refs.showMoreButton.setAttribute('aria-expanded', this.dataset.expanded);

    this.#animateHeight(startHeight, endHeight);
  };
}

if (!customElements.get('show-more-component')) {
  customElements.define('show-more-component', ShowMoreComponent);
}

